// ⚙️ 개인 설정 동기화 — 병합 계획(순수 함수). prefsSync.ts 가 쓰고 scripts/check-prefs-sync.mjs 가 node 로 직접 검증한다.
//   🔴 import 없이 유지(브라우저 의존 금지) — 회귀 스크립트가 .ts 를 그대로 읽는다.
//
//   규칙(키별, 최근 변경 우선 = LWW):
//   · 서버 at > 로컬 at → 서버값 반영(값이 같으면 at 만 맞춤)
//   · 로컬 at > 서버 at → 서버로 push(오프라인 변경·push 실패 복구)
//   · 서버에 키 없음 + 로컬에 **기본값이 아닌** 값 → 첫 동기화 seed(at=now) — 기본값 기기가 먼저 로그인해 선택 기기를 덮지 않게
//   · 로컬에서 지운 키는 '' 로 올린다(서버는 문자열만) — '없음' 을 다른 기기가 구분하게

export interface SyncedPref { key: string; defaults: Array<string | null> }
export interface Entry { v: string; at: number }

export interface ReconcileInput {
	synced: SyncedPref[];
	server: Record<string, Entry | undefined>;
	local: Record<string, string | null>;      // localStorage 값(없으면 null)
	localAt: Record<string, number>;           // 이 기기의 키별 변경 시각
	pushBack: boolean;                         // pull 이면 true(로컬이 더 새로운 키·seed 를 올린다), push 응답 처리면 false
	now: number;
	/** 이 기기의 마지막 동기화 계정과 다른 사용자가 로그인했다(owner 불일치) — 로컬에 남은 KEEP 값(테마)을 이 계정에 seed 하지 않고,
	 *  서버에 없는 키는 기본값('' 반영)으로 되돌린다. 로컬 at 은 로그아웃 때 지워져 0 이므로 '로컬이 더 새로움' 도 성립하지 않는다. */
	adopt?: boolean;
}
export interface ReconcilePlan {
	apply: Array<{ key: string; v: string }>;  // 로컬에 반영할 서버값(값이 실제로 다른 키만)
	push: Record<string, Entry>;               // 서버에 올릴 키
	at: Record<string, number>;                // 갱신된 로컬 at 맵(그대로 저장)
}

export function planReconcile(input: ReconcileInput): ReconcilePlan {
	const at: Record<string, number> = { ...input.localAt };
	const apply: ReconcilePlan['apply'] = [];
	const push: Record<string, Entry> = {};
	for (const p of input.synced) {
		const k = p.key;
		const s = input.server[k];
		const sAt = s && typeof s.v === 'string' ? Number(s.at) || 0 : 0;
		const lAt = at[k] || 0;
		const lv = input.local[k] ?? null;
		if (s && sAt > lAt) {
			if ((lv ?? '') !== s.v) apply.push({ key: k, v: s.v });
			at[k] = sAt;
			continue;
		}
		if (input.adopt) {
			// 계정이 바뀐 기기: 서버에 없는 키인데 로컬에 비기본값이 남아 있으면(이전 사용자 것) 기본값으로 되돌린다
			if (!s && lv !== null && !p.defaults.includes(lv)) apply.push({ key: k, v: '' });
			continue;
		}
		if (!input.pushBack) continue;
		if (lAt > sAt) push[k] = { v: lv ?? '', at: lAt };
		else if (!s && lv !== null && !p.defaults.includes(lv)) { at[k] = input.now; push[k] = { v: lv, at: input.now }; }
	}
	return { apply, push, at };
}

/** push 응답 처리 — 서버가 받아 준 키(값이 같음)는 서버 at 으로 로컬 at 을 맞춘다(시계 앞선 기기의 미래 at 을 서버가 눌렀을 때 매 pull 재푸시 방지). */
export function alignAtAfterPush(pushed: Record<string, Entry>, server: Record<string, Entry | undefined>, local: Record<string, string | null>, localAt: Record<string, number>): Record<string, number> {
	const at = { ...localAt };
	for (const [k, e] of Object.entries(server)) {
		if (!(k in pushed) || !e) continue;
		if ((local[k] ?? '') === e.v) at[k] = Number(e.at) || at[k] || 0;
	}
	return at;
}
